import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Stack, Text } from '@chakra-ui/react';
import type { Organization } from '@/api/Organization';
import type { User } from '@/api/User';
import type { UsersListResponse } from '@/api/UsersListResponse';
import { useAuthContext } from '@/auth/AuthContext';
import Content from '@/components/Content';
import Country from '@/components/Country';
import DataTable, { DataTableToolbar, dateCell, type DataTableColumnDef } from '@/components/DataTable';
import RoleBadge from '@/components/RoleBadge';
import SectionPanel from '@/components/SectionPanel';
import StatusBadge from '@/components/StatusBadge';
import WaitingSpinner from '@/components/WaitingSpinner';
import { countries } from '@/data/Countries';
import { formatBoardTimestamp, parseIso } from '@/data/DateTimeUtil';
import { OrgUtil } from '@/data/OrgUtil';
import { organizationService } from '@/service/OrganizationService';
import { userService } from '@/service/UserService';

const columns: DataTableColumnDef<Organization>[] = [
    { id: 'id', header: 'ID', accessorKey: 'id' },
    { id: 'name', header: 'Name', accessorKey: 'name' },
    { id: 'status', header: 'Status', accessorKey: 'status' },
    { id: 'creationMethod', header: 'Erstellungsmethode', accessorKey: 'creationMethod' },
    { id: 'createdAt', header: 'Erstellt', accessorKey: 'createdAt', cell: dateCell() },
    { id: 'currency', header: 'Währung', accessorKey: 'currency'},
    {
        id: 'country', header: 'Land',
        accessorFn: row => countries[row.country] ?? row.country,
        cell: ({ row }) => row.original.country
            ? <Country code={row.original.country} name={countries[row.original.country] ?? row.original.country} />
            : '-',
    },
    { id: 'city', header: 'Stadt', accessorFn: row => row.city || '-' },
];

type RecentUserActivity = User & { accessedAt: Date; organizationName: string };

const activityColumns: DataTableColumnDef<RecentUserActivity>[] = [
    { id: 'name', header: 'Name', accessorFn: row => `${row.firstName} ${row.lastName}`.trim() },
    { id: 'email', header: 'E-Mail', accessorKey: 'email' },
    { id: 'organization', header: 'Organisation', accessorKey: 'organizationName' },
    { id: 'role', header: 'Rolle', accessorKey: 'role', cell: ({ row }) => <RoleBadge role={row.original.role} /> },
    { id: 'status', header: 'Status', accessorKey: 'status', cell: ({ row }) => <StatusBadge status={row.original.status} /> },
    {
        id: 'accessedAt', header: 'Letzter Zugriff', accessorKey: 'accessedAt',
        cell: ({ row }) => formatBoardTimestamp(row.original.accessedAt),
    },
];

const RecentUserActivityList = ({ organizations }: { organizations: Organization[] }) => {
    const [response, setResponse] = useState<UsersListResponse>({ results: [], audit: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        userService.listRecent()
            .then(result => { if (!cancelled) setResponse(result); })
            .catch(() => { if (!cancelled) setHasError(true); })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const data = useMemo<RecentUserActivity[]>(() => {
        const users = new Map(response.results.map(user => [user.id, user]));
        const organizationNames = new Map(organizations.map(organization => [organization.id, organization.name]));
        return response.audit.flatMap(audit => {
            const user = users.get(audit.userId);
            return user ? [{
                ...user,
                accessedAt: parseIso(audit.accessedAt),
                organizationName: organizationNames.get(user.organizationId) ?? `#${user.organizationId}`,
            }] : [];
        });
    }, [response, organizations]);

    return (
        <SectionPanel title='Letzte Benutzeraktivitäten'>
            <DataTableToolbar resultCount={isLoading || hasError ? undefined : data.length}>{null}</DataTableToolbar>
            {isLoading ? <WaitingSpinner size='md' /> : hasError ? (
                <Text role='alert' color='fg.error'>Benutzeraktivitäten konnten nicht geladen werden.</Text>
            ) : data.length === 0 ? (
                <Text color='fg.muted'>Keine Benutzeraktivitäten vorhanden.</Text>
            ) : (
                <DataTable columns={activityColumns} data={data} initialSortByColumn='accessedAt' initialSortByDesc
                    hideColumnsOnXs={['email', 'role', 'status']}/>
            )}
        </SectionPanel>
    );
};

const OrganizationList = () => {
    const [data, setData] = useState<Organization[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        organizationService.listAll()
            .then(organizations => { if (!cancelled) setData(organizations); })
            .catch(() => { if (!cancelled) setHasError(true); })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, []);

    return (
        <Content title='Alle Organisationen'>
            <Stack gap='6'>
                <SectionPanel title='Organisationen'>
                    <DataTableToolbar resultCount={isLoading || hasError ? undefined : data.length}>{null}</DataTableToolbar>
                    {isLoading ? <WaitingSpinner size='md' /> : hasError ? (
                        <Text role='alert' color='fg.error'>Organisationen konnten nicht geladen werden.</Text>
                    ) : (
                        <DataTable columns={columns} data={data} initialSortByColumn='id' initialSortByDesc={false}
                            hideColumnsOnXs={['createdAt', 'creationMethod', 'currency']}/>
                    )}
                </SectionPanel>
                <RecentUserActivityList organizations={data} />
            </Stack>
        </Content>
    );
};

const AllOrganizations = () => {
    const { user } = useAuthContext();
    if (!user) return <Navigate to='/login' replace />;
    if (!OrgUtil.isRoleRoot(user.data.role)) return <Navigate to='/' replace />;
    return <OrganizationList key={user.token} />;
};

export default AllOrganizations;
